import { h, Fragment } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { Scrollbar } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { autoDeleteGwpFromCart, convertToInteger, handleAddGwpToCart, isCampaignSatisfy } from 'utils/gwp';
import GwpPromoBanner from './gwp-promo-banner';
import GwpMultipleProductItem from './gwp-multiple-product-item';
import { getGwpDismissedIds } from './gwp-session-storage';

const MAX_REQUESTS = 10;

const isCurrentGwpReady = (currentGwp) =>
  (!!currentGwp?.product &&
    !!currentGwp?.product?.available &&
    ((currentGwp?.campaignType === 'purchase' && !!convertToInteger(currentGwp?.purchaseThreshold)) ||
      (currentGwp?.campaignType === 'quantity' && !!convertToInteger(currentGwp?.quantityThreshold)))) ||
  false;

const GwpAutoAddBanner = ({
  cart,
  gwpAutoAddData,
  setGwpAutoAddDeletedIds,
  setGwpDismissedIds,
  // GwpMultiple props
  gwpMultipleId,
  gwpMultipleIdx,
  isGwpMultipleReady,
  gwpMultipleGiftLimit,
  gwpMultipleBannerTitle,
  gwpMultipleProductTitle,
  gwpMultiplePromoMessage,
  gwpMultipleProductMessage,
  gwpMultipleCampaignImage,
  gwpMultipleProductList,
  gwpMultipleProductTitle1,
  gwpMultipleProductTitle2,
  gwpMultipleProductTitle3,
  gwpMultipleProductSubtitle,
  gwpMultipleProductBadge,
  isGwpMultipleSatisfy,
  totalQuantityOfGiftProductsInCart,
  gwpDeletedFromCartCount,
  setGwpDeletedFromCartCount,
}) => {
  const [gwpArray, setGwpArray] = useState(
    gwpAutoAddData?.map((item) => ({
      ...item,
      type: 'auto-add',
      isReady: isCurrentGwpReady(item),
      shouldShow: false,
      deletedGwp: null,
      countRequests: 0,
    })) || [],
  );
  const [selectedProduct, setSelectedProduct] = useState(gwpMultipleProductList?.[0] || null);
  const isWideScreen = window ? window.innerWidth > 768 : false;

  useEffect(() => {
    if (cart?.loading) {
      return;
    }

    // Read directly from session storage to get the latest value (avoid stale prop issue)
    const currentDismissedIds = getGwpDismissedIds();

    const gwpArrayNew = [];
    const productsShouldAddToCart = [];
    gwpArray?.forEach((item) => {
      if (!item.isReady) {
        return;
      }

      const gwpCurrentLineItems = cart?.state?.items?.filter(
        (lineItem) =>
          String(lineItem?.properties?._gwp_current_id) === String(item.id) &&
          lineItem?.product_id === item?.product?.id,
      );
      const totalQuantityForCurrentGwp =
        gwpCurrentLineItems?.reduce((total, lineItem) => total + lineItem.quantity, 0) || 0;

      const isSatisfy = isCampaignSatisfy(
        cart,
        item.id,
        item.campaignType,
        convertToInteger(item.purchaseThreshold),
        convertToInteger(item.quantityThreshold),
        convertToInteger(item.giftLimit),
        totalQuantityForCurrentGwp,
        item.tagFilterType,
        item.allowedProductTags,
        item.excludedProductTags,
        '_gwp',
      );

      // Use currentDismissedIds from session storage instead of stale prop
      let deletedGwp = currentDismissedIds.find((delId) => String(delId) === String(item?.id)) || null;

      if (isSatisfy && !deletedGwp) {
        productsShouldAddToCart.push({ ...item.product, gwpCurrentId: item.id });
      }

      if (!isSatisfy) {
        setGwpAutoAddDeletedIds((prev) => {
          const newIds = [...prev.filter((delId) => String(delId) !== String(item.id))];
          setGwpDismissedIds(newIds); // Update session storage when clearing dismissed items
          return newIds;
        });
        deletedGwp = null;
      }

      gwpArrayNew.push({
        ...item,
        type: 'auto-add',
        shouldShow: totalQuantityForCurrentGwp < item.giftLimit && (!isSatisfy || !!deletedGwp),
        deletedGwp,
        isReady: item.countRequests < MAX_REQUESTS,
        countRequests: totalQuantityForCurrentGwp === 0 ? item.countRequests + 1 : 0,
      });

      autoDeleteGwpFromCart(
        gwpCurrentLineItems,
        item.id,
        item.campaignType,
        convertToInteger(item.purchaseThreshold),
        convertToInteger(item.quantityThreshold),
        convertToInteger(item.giftLimit),
        totalQuantityForCurrentGwp,
        cart,
        item.tagFilterType,
        item.allowedProductTags,
        item.excludedProductTags,
        '_gwp',
      );
    });

    handleAddGwpToCart(productsShouldAddToCart, '_gwp');
    setGwpArray(() => gwpArrayNew);
  }, [cart.state.items]);

  const handleAddAfterDeletedFromCart = (gwpId) => {
    const product = gwpArray?.find((item) => String(item?.id) === String(gwpId))?.product;
    if (!product) return;
    handleAddGwpToCart({ ...product, gwpCurrentId: gwpId }, '_gwp');
    setGwpAutoAddDeletedIds((prev) => {
      const newIds = [...prev.filter((item) => String(item) !== String(gwpId))];
      setGwpDismissedIds(newIds); // Update session storage when re-adding dismissed items
      return newIds;
    });
  };

  // GwpMultiple handlers
  const handleAddMultipleGwpToCart = (product) => {
    handleAddGwpToCart(product, '_multiple_gwp');
  };

  const handleAddMultipleAfterDeletedFromCart = () => {
    setGwpDeletedFromCartCount(0);
  };

  // Determine if GwpMultiple should show
  const shouldShowMultipleBanner = isGwpMultipleReady && (!isGwpMultipleSatisfy || gwpDeletedFromCartCount > 0);
  const shouldShowMultipleProductForm =
    isGwpMultipleReady &&
    isGwpMultipleSatisfy &&
    totalQuantityOfGiftProductsInCart < gwpMultipleGiftLimit &&
    gwpDeletedFromCartCount === 0;
  const shouldShowMultipleInCarousel =
    isGwpMultipleReady && totalQuantityOfGiftProductsInCart < gwpMultipleGiftLimit;

  // Merge auto-add and multiple GWP arrays
  const gwpArrayActive = gwpArray?.filter((item) => item.shouldShow && item.isReady);

  const combinedGwpArray = [...gwpArrayActive];

  // Add GwpMultiple item if it should show
  if (shouldShowMultipleInCarousel) {
    combinedGwpArray.push({
      type: 'multiple',
      id: gwpMultipleId,
      idx: gwpMultipleIdx,
      shouldShow: true,
      isReady: true,
    });
  }

  // Sort by idx
  combinedGwpArray.sort((a, b) => (a.idx || 0) - (b.idx || 0));

  return combinedGwpArray && combinedGwpArray?.length > 0 ? (
    <div className="gwp-auto-add w-full px-2 lg:px-3 py-2 bg-grey-12">
      <Swiper
        slidesPerView={isWideScreen && combinedGwpArray.length > 1 ? 1.2 : 1}
        modules={[Scrollbar]}
        spaceBetween={isWideScreen ? 10 : 6}
        scrollbar={{ draggable: true }}
        mousewheel={true}
      >
        {combinedGwpArray.map((item) => (
          <SwiperSlide className="w-full cursor-grabbing" key={item.id}>
            {item.type === 'auto-add' ? (
              <GwpPromoBanner
                id={item.id}
                bannerTitle={item.bannerTitle}
                productTitle={item.product.title}
                promoMessage={item.productPromoMessage}
                productMessage={item.productMessage}
                image={item?.product?.featuredImage || item?.campaignImage}
                mode={item?.deletedGwp ? 'offer-gift' : 'banner'}
                handleAddItem={handleAddAfterDeletedFromCart}
              />
            ) : (
              <Fragment>
                {shouldShowMultipleBanner ? (
                  <GwpPromoBanner
                    className="px-3"
                    bannerTitle={gwpMultipleBannerTitle}
                    productTitle={gwpMultipleProductTitle}
                    promoMessage={gwpMultiplePromoMessage}
                    productMessage={gwpMultipleProductMessage}
                    image={gwpMultipleCampaignImage}
                    mode={gwpDeletedFromCartCount > 0 ? 'offer-gift' : 'banner'}
                    handleAddItem={handleAddMultipleAfterDeletedFromCart}
                  />
                ) : null}
                {shouldShowMultipleProductForm ? (
                  <GwpMultipleProductItem
                    productMessage={gwpMultipleProductMessage}
                    productTitle={gwpMultipleProductTitle}
                    productList={gwpMultipleProductList}
                    productTitle1={gwpMultipleProductTitle1}
                    productTitle2={gwpMultipleProductTitle2}
                    productTitle3={gwpMultipleProductTitle3}
                    productSubtitle={gwpMultipleProductSubtitle}
                    productBadge={gwpMultipleProductBadge}
                    image={gwpMultipleCampaignImage}
                    selectedProduct={selectedProduct}
                    setSelectedProduct={setSelectedProduct}
                    handleAddItem={handleAddMultipleGwpToCart}
                  />
                ) : null}
              </Fragment>
            )}
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  ) : null;
};

export default GwpAutoAddBanner;
